import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileText, ListChecks, GitCompare, BookOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface Template {
  id: string;
  name: string;
  icon: typeof FileText;
  template: string;
  description: string;
}

const templates: Template[] = [
  {
    id: 'analyze',
    name: 'Analyze and Summarize',
    icon: FileText,
    description: 'Analyze content and provide a structured summary',
    template: `Based on the sprint objective: {{objective}}

Please analyze the following information and provide:
1. A comprehensive summary
2. Key insights and patterns
3. Actionable recommendations

Context from Inform stage:
{{informed_content}}

Selected decision:
{{selected_decision}}`
  },
  {
    id: 'implementation',
    name: 'Implementation Plan',
    icon: ListChecks,
    description: 'Generate a detailed step-by-step implementation plan',
    template: `Sprint Objective: {{objective}}

Create a detailed implementation plan including:
1. High-level approach
2. Step-by-step execution plan
3. Resource requirements
4. Potential challenges and mitigation strategies
5. Success metrics

Decision framework:
{{selected_decision}}

Additional context:
{{informed_content}}`
  },
  {
    id: 'compare',
    name: 'Compare Approaches',
    icon: GitCompare,
    description: 'Compare multiple approaches and recommend the best option',
    template: `Objective: {{objective}}

Compare different approaches to achieve this objective:

1. List 3-5 distinct approaches
2. For each approach, provide:
   - Pros and cons
   - Complexity assessment
   - Time/resource requirements
3. Recommend the best approach with justification

Guiding decision:
{{selected_decision}}

Reference material:
{{informed_content}}`
  },
  {
    id: 'guide',
    name: 'Step-by-Step Guide',
    icon: BookOpen,
    description: 'Create a comprehensive how-to guide',
    template: `Create a step-by-step guide for: {{objective}}

Format:
1. **Overview**: Brief explanation of what we're achieving
2. **Prerequisites**: What's needed to get started
3. **Steps**: Detailed, numbered steps with explanations
4. **Examples**: Concrete examples for each major step
5. **Troubleshooting**: Common issues and solutions
6. **Next Steps**: What to do after completion

Strategic approach:
{{selected_decision}}

Supporting information:
{{informed_content}}`
  }
];

interface PromptTemplateSelectorProps {
  onSelectTemplate: (template: string) => void;
  objective: string;
  selectedDecision?: string;
}

export const PromptTemplateSelector = ({ 
  onSelectTemplate, 
  objective,
  selectedDecision 
}: PromptTemplateSelectorProps) => {
  const [open, setOpen] = React.useState(false);
  
  const fillTemplate = (templateStr: string) => {
    return templateStr
      .replace(/\{\{objective\}\}/g, objective)
      .replace(/\{\{selected_decision\}\}/g, selectedDecision || 'No decision selected yet')
      .replace(/\{\{informed_content\}\}/g, 'Content from Inform stage');
  };

  const handleSelectTemplate = (template: string) => {
    onSelectTemplate(fillTemplate(template));
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Use Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Prompt Templates</DialogTitle>
          <DialogDescription>
            Choose a template to get started quickly. You can customize it before executing.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-3 mt-4">
          {templates.map((template) => {
            const Icon = template.icon;
            return (
              <Card
                key={template.id}
                className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleSelectTemplate(template.template)}
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-primary mt-1" />
                  <div className="flex-1">
                    <h4 className="font-semibold mb-1">{template.name}</h4>
                    <p className="text-sm text-muted-foreground">{template.description}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
